import React, { type CSSProperties } from 'react';
import Grid from '@material-ui/core/Grid';
import { formatInTimeZone } from 'date-fns-tz';
import { format, parseISO } from 'date-fns';
import { InputLabel } from '@material-ui/core';
import { Link } from 'react-router-dom';
import { legacyParse, convertTokens } from '@date-fns/upgrade/v2';

import { currencyFormat } from 'utils/currencyFormat';
import Img from 'retired/Img';
import ArrowRightIcon from 'assets/icons/arrow-right.svg';
import { blackish, lightOrange } from 'utils/color-styles';
import getDeedPathSlugFromType from 'utils/getDeedPathSlugFromType';
import { DeedListDateInfoWithStatus } from 'components/DeedListDateInfoWithStatus';
import WalletTransactionType from 'entities/walletTransaction/model';
import { Chip } from 'components/Chip';
import { deedStyles } from 'containers/VolunteerPage/deedStyles';

const dateStyles: CSSProperties = {
  width: 100,
  borderRight: `1px solid ${lightOrange}`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  textTransform: 'uppercase',
  position: 'relative',
};

type Props = {
  walletTransaction: WalletTransactionType;
};

const WalletTransaction = ({ walletTransaction }: Props) => (
  <div style={deedStyles}>
    <div style={dateStyles}>
      {walletTransaction.publishedAt ? (
        <DeedListDateInfoWithStatus date={walletTransaction.publishedAt} />
      ) : (
        '-'
      )}
    </div>
    <div style={{ flexGrow: 1, marginLeft: 28 }}>
      <Grid container alignItems="center">
        {walletTransaction.deed && (
          <Grid item xs style={{ height: '100%', margin: '0 !important' }}>
            <h3
              style={{
                fontSize: 16,
                fontWeight: 500,
                margin: '0px 0px 7px',
              }}
            >
              {walletTransaction.deed.name}
            </h3>
            {walletTransaction.deed.locationObject && (
              <div style={{ fontSize: 12 }}>
                {[
                  walletTransaction.deed.locationObject.area,
                  walletTransaction.deed.locationObject.city,
                  walletTransaction.deed.locationObject.state,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </div>
            )}
            {!!walletTransaction.deed.endingAt && (
              <div style={{ fontSize: 12, fontWeight: 300 }}>
                {formatInTimeZone(
                  walletTransaction.deed.startingAt,
                  walletTransaction.deed.timeZone,
                  convertTokens('dddd, h:mmA')
                )}
                -
                {formatInTimeZone(
                  walletTransaction.deed.endingAt,
                  walletTransaction.deed.timeZone,
                  convertTokens('h:mmA')
                )}
              </div>
            )}
          </Grid>
        )}
        {!walletTransaction.deed && walletTransaction.note && (
          <Grid item xs style={{ height: '100%', margin: '0 !important' }}>
            <h3
              style={{
                fontSize: 16,
                fontWeight: 500,
                margin: '0px 0px 7px',
              }}
            >
              {walletTransaction.note}
            </h3>
            <div style={{ fontSize: 12, fontWeight: 300 }}>
              {format(legacyParse(walletTransaction.publishedAt), convertTokens('dddd, h:mmA'))}
            </div>
          </Grid>
        )}
        <Grid item xs>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              height: 93,
              paddingTop: 5,
              overflow: 'hidden',
              alignItems: 'flex-end',
            }}
          />
        </Grid>
      </Grid>
    </div>
    {walletTransaction.createdAt && (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          margin: '0 16px',
        }}
      >
        <span>Issued Date:</span>
        <Chip
          color="success"
          label={format(parseISO(walletTransaction.createdAt), 'MMM dd, yyyy')}
        />
      </div>
    )}
    {walletTransaction.expiryDate && (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          margin: '0 16px',
        }}
      >
        <span>Expiration Date:</span>
        <Chip
          color="error"
          label={format(parseISO(walletTransaction.expiryDate), 'MMM dd, yyyy')}
        />
      </div>
    )}
    <div style={{ marginRight: 28, marginTop: 18, textAlign: 'right' }}>
      <InputLabel style={{ fontSize: 12 }} shrink>
        Issued
      </InputLabel>
      <div style={{ fontSize: 18, fontWeight: 500 }}>
        {currencyFormat(walletTransaction.creditAmount, walletTransaction.currencyCode)}
      </div>
    </div>
    {!!walletTransaction.deed && (
      <div style={{ lineHeight: '100px' }}>
        <Img
          src={ArrowRightIcon}
          alt=""
          style={{
            height: 24,
            verticalAlign: 'middle',
            marginLeft: 38,
            marginRight: 38,
          }}
        />
      </div>
    )}
  </div>
);

const WalletTransactionWithLink = ({ walletTransaction }: Props) => {
  if (!walletTransaction.deed) {
    return <WalletTransaction walletTransaction={walletTransaction} />;
  }

  return (
    <Link
      style={{ color: blackish }}
      to={`/deeds/${getDeedPathSlugFromType(walletTransaction.deed.type)}/${
        walletTransaction.deed.id
      }`}
    >
      <WalletTransaction walletTransaction={walletTransaction} />
    </Link>
  );
};

export default WalletTransactionWithLink;
